import { parseDate } from "@progress/kendo-intl";
import { Calendar } from "@progress/kendo-react-dateinputs";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { Error, Label } from "@progress/kendo-react-labels";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import configApi from "../../../api/configuration";
import contractsApi from "../../../api/contracts";
import { useApi, useFetch } from "../../../hooks/useApi";
import DateUtility from "../../../utilities/dateUtilities";
import { requiredValidator } from "../../../validators";
import { LoadingIndicator } from "../../LoadingIndicator";
import { FormInput, FormNumericTextBox } from "../../form";
import { FormChipList } from "../../form/FormChipList";
import { FormDatePicker } from "../../form/FormDatePicker";
import HolidayCalendar from "../../utilities/HolidayCalendar";
import InfoIconAndTooltip from "../../utilities/InfoIconAndTooltip";

interface IAccumulatorStartProps {
  formRenderProps: FormRenderProps;
  product: any;
  lookups: {
    doubleUpTypes: any[];
    knockOutTypes: any[];
    settlementTypes: any[];
    types: any[];
  };
}

export const AccumulatorStartStep = ({
  formRenderProps,
  product,
  lookups,
}: IAccumulatorStartProps) => {
  const { commodity, instrument, company } = product;
  const [loading, setLoading] = useState(false);
  const [contractMinDate, setContractMinDate] = useState<Date | null>(null);
  const [contractMaxDate, setContractMaxDate] = useState<Date | null>(null);
  const [selectedContract, setSelectedContract] = useState<Date | null>(null);

  // Set today to start of day
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [settlementMaxDate, setSettlementMaxDate] = useState<Date | null>(null);

  // Restore expiry date from form state when component loads
  useEffect(() => {
    const storedExpiryDate = formRenderProps.valueGetter("expiryDate");
    if (storedExpiryDate) {
      setExpiryDate(new Date(storedExpiryDate));
    }
  }, []);

  const startMaxDate = useMemo(() => {
    if (!expiryDate) return null;
    const startMax = new Date(expiryDate);
    startMax.setDate(startMax.getDate() - 1);
    return startMax;
  }, [expiryDate]);

  // These validators inexplicably break the rendering of the contract calendar
  // const validateStartDate = (value: Date | null) => {
  //   if (!value) return "Start date is required";
  //   if (value < today) return "Start date must be today or later";
  //   if (startMaxDate && value > startMaxDate)
  //     return "Start date must be before contract expiration";
  //   return null;
  // };

  // const validateSettlementDate = (value: Date | null) => {
  //   if (!value) return "Settlement date is required";
  //   if (settlementMinDate && value < settlementMinDate)
  //     return "Settlement date must be on or after contract expiration";
  //   if (settlementMaxDate && value > settlementMaxDate)
  //     return "Settlement date must be within 30 days of contract expiration";
  //   return null;
  // };

  const {
    data: expirationDates,
    isPending: expirationDatesLoading,
    error: expirationDatesError,
  } = useQuery({
    queryKey: ["expirationDates", commodity.id],
    queryFn: () => contractsApi.getExpirationDates(commodity.id),
    select: (data) =>
      data.expirationDates.map((item) => ({
        observationDate: parseDate(item.observationDate) || new Date(),
        expiryDate: parseDate(item.expiryDate) || new Date(),
      })) || [],
    enabled: !!commodity.id,
  });

  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
    request: requestBoundaries,
  } = useApi(contractsApi.getDateBoundaries);

  const {
    data: configData,
    loading: configLoading,
    error: configError,
  } = useFetch(
    configApi.getConfiguration,
    company.id,
    commodity.id,
    instrument.id,
    instrument.productType,
  );

  // Fetch holidays (same as HolidayCalendar)
  const { data: holidaysData, isPending: holidaysLoading } = useQuery({
    queryKey: ["holidays"],
    queryFn: () => contractsApi.getHolidays(),
  });
  const holidays = holidaysData?.holidays || [];

  useEffect(() => {
    const isLoading = contractLoading || configLoading || expirationDatesLoading;
    setLoading(isLoading);
    formRenderProps.onChange("loaded", { value: !isLoading });
  }, [contractLoading, configLoading, expirationDatesLoading]);

  useEffect(() => {
    setContractMinDate(parseDate(contractData?.minDate || ""));
    setContractMaxDate(parseDate(contractData?.maxDate || ""));
  }, [contractData]);

  useEffect(() => {
    const id = configData?.configurationId || 0;
    formRenderProps.onChange("configurationId", { value: id });
    requestBoundaries(commodity?.abbreviation, id);
  }, [configData]);

  // Update selectedContract when contract value changes
  const contractValue = formRenderProps.valueGetter("contract");

  useEffect(() => {
    if (contractValue) {
      const newContract = new Date(contractValue);
      const isNewContract =
        !selectedContract || selectedContract.getTime() !== newContract.getTime();

      if (isNewContract) {
        setSelectedContract(newContract);

        // Find matching expiration date and update range max date
        if (expirationDates) {
          const matchingExpiration = expirationDates.find(
            (date) => date.observationDate.getTime() === newContract.getTime(),
          );

          if (matchingExpiration) {
            // Settlement date can start on expiry
            setExpiryDate(matchingExpiration.expiryDate);

            // Store expiry date in form state so it persists between steps
            formRenderProps.onChange("expiryDate", { value: matchingExpiration.expiryDate });

            // Set settlement date max to 30 days after expiry
            const endMaxDate = new Date(matchingExpiration.expiryDate);
            endMaxDate.setDate(endMaxDate.getDate() + 30);
            setSettlementMaxDate(endMaxDate);

            // Compute next business day after today
            const nextBusinessDay = DateUtility.getNextBusinessDay(today, holidays);

            // Only reset start date if it's invalid for the new contract
            // Don't reset if we're just returning to this step
            const currentStart = formRenderProps.valueGetter("start");
            const startMax = startMaxDate; // Use the derived startMaxDate

            // Only reset if the current start date is completely invalid for the new contract
            if (
              !currentStart ||
              currentStart < nextBusinessDay ||
              (startMax && currentStart > startMax)
            ) {
              formRenderProps.onChange("start", { value: nextBusinessDay });
            }

            // Always set settlement date to expiry when contract changes
            formRenderProps.onChange("settlement", { value: matchingExpiration.expiryDate });
          }
        }
      }
    }
  }, [contractValue, expirationDates, holidays]);

  return (
    <div className="stepper-form" style={{ display: "flex", justifyContent: "center" }}>
      <div style={{ width: "60%" }}>
        <Field
          key={"quoteName"}
          id={"quoteName"}
          name={"quoteName"}
          label={"Quote Name"}
          component={FormInput}
          validator={requiredValidator}
        />
        <LoadingIndicator loading={loading} />

        <Error>{configError}</Error>
        <Error>{contractError}</Error>
        <Error>{expirationDatesError?.message}</Error>

        {!loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              <Field
                key={"instrumentTypeId"}
                id={"instrumentTypeId"}
                name={"instrumentTypeId"}
                label={"Type"}
                component={FormChipList}
                data={lookups.types}
                selection={"single"}
                validator={requiredValidator}
              />
              <Field
                key={"knockOutTypeId"}
                id={"knockOutTypeId"}
                name={"knockOutTypeId"}
                label={"Knock-Out"}
                component={FormChipList}
                data={lookups.knockOutTypes}
                selection={"single"}
                validator={requiredValidator}
              />

              <Field
                key={"doubleUpTypeId"}
                id={"doubleUpTypeId"}
                name={"doubleUpTypeId"}
                label={"Double Up"}
                component={FormChipList}
                data={lookups.doubleUpTypes}
                selection={"single"}
                validator={requiredValidator}
              />

              <Field
                key={"settlementTypeId"}
                id={"settlementTypeId"}
                name={"settlementTypeId"}
                label={"Fixing Frequency"}
                component={FormChipList}
                data={lookups.settlementTypes}
                selection={"single"}
                validator={requiredValidator}
              />
            </div>
            <div>
              <Field
                key={"volume"}
                id={"volume"}
                name={"volume"}
                label={`Volume (${commodity?.units})`}
                component={FormNumericTextBox}
                spinners={false}
                validator={requiredValidator}
                format={"n0"}
                min={0}
              />
              <Field
                key={"contract"}
                id={"contract"}
                name={"contract"}
                label={"Contract Month"}
                component={FormDatePicker}
                calendar={(props: any) => <Calendar bottomView="year" topView="year" {...props} />}
                min={contractMinDate || undefined}
                max={contractMaxDate || undefined}
                format={"MMM yyyy"}
                wrapperStyle={{ maxWidth: 200 }}
              />

              <Field
                key={"start"}
                id={"start"}
                name={"start"}
                label={
                  <span>
                    First Fixing Date
                    <InfoIconAndTooltip>
                      This defaults to the next business day after today, but it can be set up to
                      the day before the last fixing date.
                    </InfoIconAndTooltip>
                  </span>
                }
                component={FormDatePicker}
                min={DateUtility.getNextBusinessDay(today, holidays)}
                max={startMaxDate || undefined}
                format={"dd MMM yyyy"}
                calendar={HolidayCalendar}
                disabled={!selectedContract}
                validator={requiredValidator}
              />
              {expiryDate && (
                <div style={{ marginTop: 10 }}>
                  <Label>
                    Last Fixing Date:
                    <InfoIconAndTooltip>
                      If it exists, this is the first position date for the selected contract month.
                      Otherwise, it is the last business day of month prior to the selected contract
                      month.
                    </InfoIconAndTooltip>
                    &nbsp;{DateUtility.formatShortDate(expiryDate)}
                  </Label>
                </div>
              )}
              <Field
                key={"settlement"}
                id={"settlement"}
                name={"settlement"}
                label={
                  <span>
                    Settlement Date
                    <InfoIconAndTooltip>
                      This defaults to the last fixing date, but can be set to up to 30 days later.
                    </InfoIconAndTooltip>
                  </span>
                }
                component={FormDatePicker}
                min={expiryDate || undefined}
                max={settlementMaxDate || undefined}
                format={"dd MMM yyyy"}
                calendar={HolidayCalendar}
                disabled={!selectedContract}
                validator={requiredValidator}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
